// Mark: Used in V2
import { Link as GatsbyLink } from "gatsby"
import React, { useMemo } from "react"

import { linkIsExternalUrl, getUrlFromStoryblokLink } from "@utils/storyblok"
import { appendQueryParams, mergeQueryParams } from "@utils/url"

export type CTATrackingEvent = {
  trackingEvent: string
  trackingEventKey?: string
  trackingEventValue?: string
}

type Props = React.HTMLAttributes<HTMLAnchorElement> & {
  children: any
  link: string | Storyblok.Link | YocoCom.YocoLink
  queryParams?: string
  opensNewTab?: boolean
  onClick?: React.MouseEventHandler<HTMLElement>
  className?: string
  "aria-label"?: string
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
  onFocus?: React.FocusEventHandler<HTMLElement>
  "data-testid"?: string
}

const getLinkUrl = (link: Props["link"]): string => {
  if (typeof link === "string") {
    return link
  }

  return "linktype" in link ? getUrlFromStoryblokLink(link) : link.url
}

const getLinkTarget = (link: Props["link"]): string | undefined => {
  if (typeof link === "string") {
    return undefined
  }

  return link.target
}

const getQueryParams = (
  link: string | Storyblok.Link | YocoCom.YocoLink,
  queryParams?: string
): string | undefined => {
  if (typeof link === "string") {
    return queryParams
  }

  if ("queryParams" in link) {
    return mergeQueryParams(queryParams, link.queryParams)
  }

  return queryParams
}

/*
  Usage note: This Link element should be used to augment
  another element or component to turn it into a link where
  we cannot predict whether the link target of the link will
  be internal or external. E.g. an image or custom card may be
  wrapped in this Link.
*/
const Link = React.forwardRef<HTMLAnchorElement, Props>(
  (
    {
      children,
      link,
      queryParams,
      opensNewTab = false,
      onClick,
      className,
      "aria-label": ariaLabel,
      onMouseEnter,
      onFocus,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    const linkTarget = getLinkTarget(link)
    const shouldOpenInNewTab = opensNewTab || linkTarget === "_blank"

    const href = useMemo(() => {
      if (link) {
        let url = getLinkUrl(link)

        const activeQueryParams = getQueryParams(link, queryParams)

        if (activeQueryParams) {
          url = appendQueryParams(url, activeQueryParams)
        }

        return url
      }

      return undefined
    }, [link, queryParams])

    const isExternal = linkIsExternalUrl(link)

    if (shouldOpenInNewTab) {
      return (
        <a
          href={href}
          target={linkTarget === "_self" ? undefined : linkTarget || "_blank"}
          rel={isExternal ? "noreferrer nofollow" : "noopener noreferrer"}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={className}
          onClick={onClick}
          aria-label={ariaLabel}
          onMouseEnter={onMouseEnter}
          onFocus={onFocus}
          data-testid={dataTestId}
          {...props}
        >
          {children}
        </a>
      )
    }

    if (isExternal) {
      return (
        <a
          href={href}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={className}
          onClick={onClick}
          aria-label={ariaLabel}
          onMouseEnter={onMouseEnter}
          onFocus={onFocus}
          data-testid={dataTestId}
          {...props}
        >
          {children}
        </a>
      )
    }

    return (
      <GatsbyLink
        to={href || "/"}
        ref={ref as React.Ref<any>}
        className={className}
        onClick={onClick}
        aria-label={ariaLabel}
        onMouseEnter={onMouseEnter}
        onFocus={onFocus}
        data-testid={dataTestId}
        {...props}
      >
        {children}
      </GatsbyLink>
    )
  }
)

Link.displayName = "Link"

export default React.memo(Link)
